import {DestroyRef, inject, Injectable, Signal, signal, WritableSignal} from '@angular/core';

/**
 * Tailwind's `md` breakpoint expressed as the query for the side **below** it.
 *
 * `767.98` rather than `767`: a viewport can be a fractional number of CSS pixels (a zoomed page, a scaled
 * display), and `max-width: 767px` leaves 767.5 matching neither this query nor Tailwind's `md:` rule. This is
 * the same off-by-a-hundredth Bootstrap and Tailwind's own `not-md` variant use.
 */
export const BELOW_MD_QUERY = '(max-width: 767.98px)';

/**
 * Tailwind's `sm` breakpoint expressed as the query for the side **below** it — phone widths.
 *
 * Same fractional bound as {@link BELOW_MD_QUERY}, for the same reason: `max-width: 639px` would leave a
 * 639.5 px viewport matching neither this query nor Tailwind's `sm:` rule, so anything switched on the pair
 * would be missing from both sides of the breakpoint at once.
 */
export const BELOW_SM_QUERY = '(max-width: 639.98px)';

/**
 * Which side of Tailwind's breakpoints the window is on, as signals.
 *
 * **Why a service and not a CSS class.** A `md:hidden` class and an ARIA decision are two independent facts
 * about the same breakpoint, and nothing keeps them in step: the role pickers rendered their description as
 * `md:hidden` text *and* let Angular Material describe the same trigger through `aria-describedby` at every
 * width, so which of the two copies actually reached a screen reader depended on a Tailwind class and an
 * `aria-hidden` attribute agreeing by hand. This signal is the single source both sides read, so "the inline
 * text is on screen" and "the tooltip is the description" cannot disagree.
 *
 * The `sm` signal ({@link isBelowSm}) exists for a stronger reason still: the context switcher renders as a
 * *different element in a different host* on the two sides of that breakpoint (#602), and only one of the two
 * may be in the DOM at a time. A pair of CSS classes cannot express that — it would leave two live components
 * mounted, each with its own `/auth/context/available` fetch and its own claim on the shared presence signal.
 *
 * **Why it is injectable.** The Karma viewport is pinned at 1280x900 (#455) and no spec may depend on that
 * number, so a breakpoint-dependent spec has to *drive* the breakpoint rather than resize the runner. Providing
 * a stub for this service is that lever — see `docs/articles/frontend/testing.md`.
 */
@Injectable({providedIn: 'root'})
export class ViewportService {
  private readonly compact = signal(false);
  private readonly belowSm = signal(false);

  /**
   * `true` while the viewport is narrower than Tailwind's `md` (768 px) — the width at which hover-only
   * affordances stop being reachable, so a hint has to be on the screen instead of behind a pointer.
   */
  readonly isCompact: Signal<boolean> = this.compact.asReadonly();

  /**
   * `true` while the viewport is narrower than Tailwind's `sm` (640 px) — phone territory, where a floating
   * corner overlay sits on top of dialog footers, drawers and the installed app's bottom tab bar.
   */
  readonly isBelowSm: Signal<boolean> = this.belowSm.asReadonly();

  constructor() {
    const destroyRef = inject(DestroyRef);

    // Guarded rather than assumed: the service is constructed by unit tests and by any future non-browser
    // render, and a missing `matchMedia` must degrade to "not compact" (the tooltip keeps describing, and the
    // switcher keeps its desktop placement) rather than throw during injection and take the whole screen with
    // it.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;

    this.track(BELOW_MD_QUERY, this.compact, destroyRef);
    this.track(BELOW_SM_QUERY, this.belowSm, destroyRef);
  }

  /** Binds one media query to one signal for the lifetime of this injector. */
  private track(media: string, into: WritableSignal<boolean>, destroyRef: DestroyRef): void {
    const query = window.matchMedia(media);
    into.set(query.matches);

    const onChange = (event: MediaQueryListEvent) => into.set(event.matches);
    query.addEventListener('change', onChange);
    destroyRef.onDestroy(() => query.removeEventListener('change', onChange));
  }
}
